import React, { useState } from 'react'
import { Box, IconButton, Paper, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { convertDateToString, checkIsBase64 } from '../../lib/helpers'
import PhotoPreviewWindow from './PhotoPreviewWindow'
import PhotoLabel from './styled/PhotoLabel'
import { PhotoTitle, PhotoWrapper } from './styled/PhotoTitle'
import PhotoEditDrawer from './PhotoEditDrawer'
import NoPhoto from './NoPhoto'
import PhotosList from './PhotosList'
import { useTranslation } from 'react-i18next'
import useVehiclePhotoCurrentSideInfo from '../../hooks/VehiclePhotos/useVehiclePhotoCurrentSide'
import { useOnImageUpload } from '../../hooks/VehiclePhotos/useOnImageUpload'
import { createContext } from 'react'
import { History } from '../../constants'

export const VehiclePhotoDialogDispatchContext = createContext({
  setVehiclePhotoDialog: (_value: any) => {},
  setVehiclePhotoPreviewDialog: (_value: any) => {},
})

const VehiclePhotos = ({
  photo,
  photos,
  tabValue,
  in_route,
  dateToday,
  fullPhotos,
  vehicleId,
}: {
  photo: string | null
  photos: History[]
  fullPhotos: History[]
  tabValue: number
  in_route: boolean
  dateToday: string
  vehicleId: string
}) => {
  const { t } = useTranslation()

  const [vehiclePhotoDialog, setVehiclePhotoDialog] = useState({ open: false })

  const [vehiclePhotoPreviewDialog, setVehiclePhotoPreviewDialog] = useState({
    open: false,
    photo: null,
  })

  const { sideKey, sideText } = useVehiclePhotoCurrentSideInfo()
  const onImageUpload = useOnImageUpload(tabValue, vehicleId)

  return (
    <VehiclePhotoDialogDispatchContext.Provider
      value={{
        setVehiclePhotoDialog: setVehiclePhotoDialog,
        setVehiclePhotoPreviewDialog: setVehiclePhotoPreviewDialog,
      }}
    >
      <Paper
        className="section"
        sx={(theme) => ({
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08)',
          padding: theme.spacing(1, 2, 2),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: 295,
          position: 'relative',
        })}
      >
        <input
          accept="image/jpeg, image/jpg, image/png"
          id="upload-photo-img"
          type="file"
          onChange={onImageUpload}
          hidden
        />
        {photos.length > 0 && fullPhotos.length > 0 ? (
          <PhotosList
            photo={photo}
            photos={photos}
            fullPhotos={fullPhotos}
            in_route={in_route}
            dateToday={dateToday}
            sideKey={sideKey}
          />
        ) : photo && dateToday ? (
          <>
            <PhotoTitle variant="h1" component="p">
              {t('photos')}
            </PhotoTitle>
            <Box width="100%" display="flex" overflow="auto">
              <PhotoWrapper>
                <Box
                  component="img"
                  src={
                    checkIsBase64(photo)
                      ? `data:image/png;base64,${photo}`
                      : photo
                  }
                  sx={{ width: '100%' }}
                  alt={t('common.today')}
                  onClick={() =>
                    setVehiclePhotoPreviewDialog({
                      open: true,
                      photo,
                      timestamp: t('common.today'),
                    })
                  }
                />
                <PhotoLabel withPhotoLabelToday>
                  <Typography variant="body1">
                    {convertDateToString(dateToday, true)}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setVehiclePhotoDialog({ open: true })}
                  >
                    <EditIcon />
                  </IconButton>
                </PhotoLabel>
              </PhotoWrapper>
            </Box>
          </>
        ) : (
          <NoPhoto sideText={sideText} inRoute={in_route} />
        )}
        <PhotoEditDrawer open={vehiclePhotoDialog.open} />
        <PhotoPreviewWindow
          title=""
          variant="dark"
          setVehiclePhotoDialog={setVehiclePhotoDialog}
          open={vehiclePhotoPreviewDialog.open}
          photoToday={photo}
          photo={vehiclePhotoPreviewDialog.photo}
          timestamp={vehiclePhotoPreviewDialog.timestamp}
          handleClose={() => setVehiclePhotoPreviewDialog({ open: false })}
          dateToday={dateToday}
        />
      </Paper>
    </VehiclePhotoDialogDispatchContext.Provider>
  )
}

export default VehiclePhotos
